import { PagePartsData } from '../pageDataReducer';
import { DataPair } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ContextData/DataPair';
import { PersonalizationArea } from '@decisions/server/DecisionsFramework/Design/Page/Personalization/PersonalizationArea';
import { IPersonalizationValue } from 'components/Editors/data-filters/hooks/useScopeHandlers';
import {
  Personalization,
  PersonalizationScope,
  PersonalizationUserScope,
} from '@decisions/server/DecisionsFramework/Design/Page/Personalization';

declare var $DP: any;

export interface SessionPersonalization {
  Page: DataPair[];
  Parts: { [key: string]: DataPair[] };
}

export function getInitialPageData(pageId: string) {
  return $DP.Portal.PageDataStore.get(pageId);
}

/**
 * @param id of page or part
 * @param isPage true if it applies to page
 * @returns id string corresponding to personalizationService.SendClientEvents
 */
export function getPersonalizationId(id: string) {
  return id;
}

/**
 * Save any session personalization to session storage, merging with any existing values
 * @param id id of page or page part
 * @param value data to store, if they are session level.
 */
export function saveSessionPersonalization(id: string, personalizations: Personalization[]) {
  const storedPersonalization = loadSessionPersonalization(id) || {};

  storedPersonalization.Parts = storedPersonalization.Parts ?? {};
  storedPersonalization.Page = storedPersonalization.Page ?? [];

  // save only session personalization to local storage.
  const sessionPersonalization = personalizations.filter(
    personalization => personalization.UserScope === PersonalizationUserScope.Session
  );

  // get all the ones stored at a part level that _come from_ page level:
  let allPageValues: DataPair[] = [];
  Object.keys(storedPersonalization.Parts).forEach(partId => {
    allPageValues.push(
      ...storedPersonalization.Parts[partId].filter(
        (ev: DataPair) => ev.OutputValue?.PersonalizationScope.Scope === PersonalizationScope.Page
      )
    );
  });

  // get all new page-level values
  let newPageValues: DataPair[] = [];
  sessionPersonalization.forEach(personalization => {
    newPageValues.push(
      ...personalization.Data.filter(p => p.OutputValue?.PersonalizationScope.Scope === PersonalizationScope.Page)
    );
  });

  // merge into an object, overwriting any old ones with new ones:
  let pageMap: { [key: string]: DataPair } = {};
  [...allPageValues, ...newPageValues].forEach(dp => {
    pageMap[dp.Name] = dp;
  });

  storedPersonalization.Page = Object.values(pageMap);

  // merge in any new part-level values before storing.
  let partsMap: { [key: string]: DataPair[] } = storedPersonalization.Parts;
  sessionPersonalization.forEach(
    ({ PartId, Data }) => (partsMap[PartId] = mergePersonalization(partsMap[PartId] || [], Data).merged)
  );

  storedPersonalization.Parts = partsMap;

  sessionStorage.setItem(id, JSON.stringify(storedPersonalization));
  return storedPersonalization;
}

/**
 * FIXME this was broken during merge storage into one entry per page.
 * Might not be needed, as merging should happen during save, now.
 * Would require a part ID target or list or partID targets to merge into.
 *
 * @param id of page/part
 * @param storedValues any saved values to merge with
 * @param isPage true if loading page-level
 * @returns merged data set, along with flag as to w
 */
export function loadAndMergeSessionPersonalization(id: string, storedValues: DataPair[] = []) {
  const value = sessionStorage.getItem(getPersonalizationId(id));
  const parsedValue: DataPair[] = value ? JSON.parse(value) : [];
  const merged = mergePersonalization(storedValues, parsedValue).merged;
  return { hadSessionStoredValues: parsedValue.length > 0, merged };
}

export function clearPersonalization(pageId: string, partId: string | null, area: PersonalizationArea) {
  // load existing ones.
  let stored: SessionPersonalization = loadSessionPersonalization(pageId);

  stored = {
    ...{ Page: [], Parts: {} },
    ...stored,
  };

  const partIds = partId ? [partId] : Object.keys(stored.Parts);

  partIds.forEach(partId => {
    stored.Parts[partId] = (stored.Parts[partId] || []).filter(
      val => (val.OutputValue as IPersonalizationValue).Area !== area
    );
  });

  stored.Page = Object.keys(stored.Parts).flatMap(partId =>
    (stored.Parts[partId] || []).filter(dp => dp.OutputValue?.PersonalizationScope.Scope === PersonalizationScope.Page)
  );

  sessionStorage.setItem(pageId, JSON.stringify(stored));
}

/**
 * Merge two arrays by DataPair name, preserving order, and placing any in the preferred list that have no match at
 * the end of the resulting array.
 * @param existing "original" values
 * @param preferred values to prefer in merge.
 * @return values, with a flag as to whether any values in the original array were replaced or not.
 */
export function mergePersonalization(existing: DataPair[], preferred: DataPair[]) {
  existing = existing ?? [];
  const merged = [...existing];
  const names = existing.map(val => val.Name);
  let replacedValue = false;

  preferred.forEach(value => {
    const i = names.indexOf(value.Name);
    if (i < 0) {
      merged.push(value);
    } else {
      merged[i] = value;
      replacedValue = true;
    }
  });

  return { replacedValue, merged };
}

export function loadPartPersonalization(initialData: PagePartsData, pageId: string) {
  const partsWithSessionPersonalization = new Map<
    string,
    {
      Personalization: DataPair[]; // capital P because of server interface
      partData: any;
    }
  >();

  if (!initialData) {
    return partsWithSessionPersonalization;
  }

  Object.keys(initialData).forEach(partId => {
    const data = initialData[partId];
    const loaded = loadAndMergeSessionPersonalization(`${pageId}_${partId}`, data.personalization);
    if (loaded.hadSessionStoredValues) {
      const Personalization = loaded.merged;
      partsWithSessionPersonalization.set(partId, {
        Personalization,
        partData: { ...data, Personalization },
      });
    }
  });

  return partsWithSessionPersonalization;
}

export function loadSessionPersonalization(pageId: string): SessionPersonalization {
  const storedString = sessionStorage.getItem(pageId);
  let value = (storedString && JSON.parse(storedString)) || {};

  // load what was stored, or else an empty default:
  return {
    Page: [],
    Parts: {},
    ...value,
  };
}

export function anyFilters(p12n: DataPair[]) {
  return !!p12n.find(pair => (pair.OutputValue as IPersonalizationValue)?.Area === PersonalizationArea.DataFilters);
}

export function anyConfig(p12n: DataPair[]) {
  return !!p12n.find(pair => (pair.OutputValue as IPersonalizationValue)?.Area === PersonalizationArea.Configuration);
}

export const PageDataHelpers = {
  saveSessionPersonalization,
  clearPersonalization,
  loadSessionPersonalization,
  mergePersonalization,
};
